import { PanoMeasureMultiWallsRenderer } from "@/components/r3f/renderers/measurements/pano-measure-multi-walls-renderer";
import { PanoramaRenderer } from "@/components/r3f/renderers/panorama-renderer";
import { selectShouldRenderBoundingBoxes } from "@/features/ai-chat/ai-selectors";
import { BoundingBoxRenderer } from "@/features/ai-chat/bounding-box-renderer";
import {
  selectDepthMapForImg360,
  selectIsPanoWithDepth,
} from "@/modes/walk-mode/walk-scene-selectors";
import { PanoObject } from "@/object-cache";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectPanoMeasurementWalls } from "@/store/measurement-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { assert } from "@faro-lotv/foundation";
import {
  IElementGenericImgSheet,
  type IElementDepthMap,
} from "@faro-lotv/ielement-types";
import {
  decodeCdiBuffer,
  EquirectangularDepthImage,
  type LodPano,
} from "@faro-lotv/lotv";
import {
  CachedWorldTransform,
  createSignedUrlForIElementUri,
} from "@faro-lotv/project-source";
import { useApiClientContext, type ProjectApi } from "@faro-lotv/service-wires";
import { Img360PanoRef } from "@faro-lotv/spatial-ui";
import { ThreeEvent } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import { JSX, useCallback, useEffect, useState } from "react";
import { Camera } from "three";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

type FlyWalkPanoRendererCommonProps = {
  /** Sheet to use to compute the pano elevation */
  sheetForElevation: IElementGenericImgSheet | undefined;
  /** Whether picking is enabled on the CAD model or not */
  pickingEnabled: boolean;
  /** Custom camera to render the pano */
  customCamera?: Camera;
  /** Callback issued when the pano transform is edited */
  onPanoTransformChanged?(transform: CachedWorldTransform): void;
};

type FlyWalkPanoRendererProps = FlyWalkPanoRendererCommonProps & {
  /** The current active panorama */
  pano: PanoObject | null;
};

/** @returns a component to render a pano object in flywalk mode */
export function FlyWalkPanoRenderer({
  pano,
  sheetForElevation,
  pickingEnabled,
  customCamera,
  onPanoTransformChanged,
}: FlyWalkPanoRendererProps): JSX.Element | null {
  if (!pano) {
    return null;
  }

  return (
    <FlyWalkPanoRendererBase
      pano={pano}
      sheetForElevation={sheetForElevation}
      pickingEnabled={pickingEnabled}
      customCamera={customCamera}
      onPanoTransformChanged={onPanoTransformChanged}
    />
  );
}

type FlyWalkPanoRendererBaseProps = FlyWalkPanoRendererCommonProps & {
  /** The current active panorama */
  pano: PanoObject;
};

function FlyWalkPanoRendererBase({
  pano,
  sheetForElevation,
  pickingEnabled,
  customCamera,
  onPanoTransformChanged,
}: FlyWalkPanoRendererBaseProps): JSX.Element {
  const isPanoWithDepth = useAppSelector(selectIsPanoWithDepth(pano.iElement));
  assert(
    !isPanoWithDepth || pano.iElement.isRotationAccurate,
    "isRotationAccurate must be true if pano has depth info",
  );

  const isAiChatAllowed = useAppSelector(selectHasFeature(Features.AiChat));
  const shouldShowBoundingBoxes = useAppSelector(
    selectShouldRenderBoundingBoxes(pano.iElement.id),
  );

  const walls = useAppSelector(
    selectPanoMeasurementWalls(pano.iElement.id),
    isEqual,
  );

  const { onModelHovered, onModelClicked } = useToolsAcquaintance();

  return (
    <>
      <PanoWithDepth
        pano={pano}
        sheetForElevation={sheetForElevation}
        pickingEnabled={pickingEnabled}
        customCamera={customCamera}
        onPanoTransformChanged={onPanoTransformChanged}
      />
      {pickingEnabled && (
        <PanoMeasureMultiWallsRenderer
          pano={pano}
          walls={walls}
          onModelClicked={onModelClicked}
          onModelHovered={onModelHovered}
        />
      )}
      {isAiChatAllowed && shouldShowBoundingBoxes && (
        <BoundingBoxRenderer pano={pano.iElement} />
      )}
    </>
  );
}

/** @returns a component that renders a LOD panorama image and loads and manages its depth information if present.  */
function PanoWithDepth({
  pano,
  sheetForElevation,
  pickingEnabled,
  customCamera,
  onPanoTransformChanged,
}: FlyWalkPanoRendererBaseProps): JSX.Element {
  const [ref, setRef] = useState<Img360PanoRef | null>(null);
  const depthMap = useAppSelector(selectDepthMapForImg360(pano.iElement));

  const { projectApiClient } = useApiClientContext();

  useEffect(() => {
    if (!ref) return;
    if (!depthMap) return;

    const controller = new AbortController();
    const connection =
      ref === pano ? undefined : ref.depthsChanged.pipe(pano.depthsChanged);
    if (ref.depths === undefined) {
      loadPanoDepthData(
        ref,
        depthMap,
        projectApiClient,
        controller.signal,
      ).catch((error) => {
        console.warn(error);
      });
    }
    return () => {
      controller.abort();
      connection?.dispose();
    };
  }, [ref, depthMap, pano, projectApiClient]);

  // Dispose heavy depth data on unmount
  useEffect(
    () => () => {
      pano.tiledPano.disposeDepths();
    },
    [pano],
  );

  const { onModelHovered, onModelClicked, onModelZoomed, isWaypointHovered } =
    useToolsAcquaintance();

  const onClick = useCallback(
    (ev: ThreeEvent<MouseEvent>) => {
      if (!isWaypointHovered) {
        onModelClicked(ev, pano.iElement.id);
      }
    },
    [isWaypointHovered, onModelClicked, pano.iElement.id],
  );

  const onPointerMove = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      onModelHovered(ev, pano.iElement.id);
    },
    [pano.iElement.id, onModelHovered],
  );

  const onWheel = useCallback(
    (ev: ThreeEvent<WheelEvent>) => {
      onModelZoomed(ev, pano.iElement.id);
    },
    [pano.iElement.id, onModelZoomed],
  );

  return (
    <PanoramaRenderer
      pano={pano}
      sheetForElevation={sheetForElevation}
      ref={setRef}
      onTransformChanged={onPanoTransformChanged}
      customCamera={customCamera}
      onClick={pickingEnabled ? onClick : undefined}
      onPointerMove={pickingEnabled ? onPointerMove : undefined}
      onWheel={pickingEnabled ? onWheel : undefined}
    />
  );
}

/**
 *
 * @param pano The pano image to load depth for
 * @param depthMap the depth map iElement
 * @param projectApi project API client class
 * @param signal Object communicating whether the depth loading was aborted
 * @returns A promise that resolves when the depth data are loaded
 */
async function loadPanoDepthData(
  pano: LodPano,
  depthMap: IElementDepthMap,
  projectApi: ProjectApi,
  signal: AbortSignal,
): Promise<void> {
  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    depthMap,
  ).requestSignedUrl();

  const ret = await fetch(signedUrl, { signal });
  if (!ret.ok) return;
  const b = await ret.blob();
  const data = await b.arrayBuffer();

  let depthData: Float32Array;
  try {
    depthData = decodeCdiBuffer(new DataView(data));
  } catch {
    depthData = new Float32Array(data);
  }

  const equirectImage = new EquirectangularDepthImage(
    depthMap.pixelWidth,
    depthData,
  );
  // Increase the offset to make the normal more stable while moving the mouse over the pano.
  // By experiments with multiple datasets, 9px is chosen as a good value results stable normal.
  equirectImage.normalComputeTrianglePointsOffset = 9;
  if (!signal.aborted) {
    pano.tiledPano.setDepths(equirectImage);
  }
}
